// src/appbuilder/chat/useUnifiedUnread.js
//
// 2026-05-07: single source of truth for "what threads does this user
// have, and what's unread on each one." Subscribes to BOTH chat
// schemas Firestore actually uses:
//
//   • DMs        — collectionGroup("inbox") under
//                  userConversations/{uid}/profiles/{pid}/inbox/{cid}
//   • Group chat — collection at userConversations/{uid}/groups/{gid}
//                  joined with the global `appGroups/{gid}` doc to
//                  pick up appId / lastText / lastAtMs / appName.
//
// Why a unified hook?
// ───────────────────
// We previously had FIVE places counting unread, all reading from
// different paths and producing different numbers:
//
//   1. AppStore card badge        — collectionGroup("inbox") only
//   2. DatingTemplate Inbox pill  — listings → per-profile inbox
//   3. InboxOverlay totalUnread   — recents.unread + groupUnreadCount
//   4. "Group Messages" row      — userConversations/{uid}/groups
//   5. LEX AI Inbox tab          — collectionGroup("inbox") only
//
// Sites #1, #2, #5 were silently MISSING group chat unread (they only
// looked at the inbox subcollection). Sites #3 + #4 had it right but
// implemented it inline. Result: the number on the AppStore card and
// the number inside the in-app Inbox could disagree by exactly the
// group-chat unread count.
//
// This hook fixes that by reading both shapes once, joining them, and
// emitting a single normalized stream the UI can aggregate any way it
// wants (per-app, total, by-thread).

import { useEffect, useMemo, useState } from "react";
import {
  collection,
  collectionGroup,
  doc,
  onSnapshot,
  query,
  limit as qLimit,
} from "firebase/firestore";
import { db } from "../../firebase";

// Lex Bot conversations live in the same `inbox/` subcollection but
// belong to the dedicated "Lex Bot" tab in LexAIPanel — never to a
// dating app's unread count. Hard-skip them here so every consumer
// (AppStore card pill, DatingTemplate Inbox pill, LexAI Inbox …) is
// consistent.
const LEX_BOT_PEER_UID = "__lextrack_ai_bot__";

// 2026-05-19: SMS threads live at
//   userConversations/{uid}/profiles/sms/inbox/{cid}
// and were getting pulled into the same DM stream as dating-app DMs
// because the collectionGroup("inbox") query is profile-agnostic. SMS
// has its own dedicated "Phone" tab in LexAIPanel (renders the Twilio
// thread directly), so it must NOT also appear in the LEX AI Inbox
// DMs view. Skip every thread whose myProfileId === SMS_PROFILE_ID.
// Same intent as the LEX_BOT_PEER_UID filter above: "this surface
// already has its own tab, don't double-list it."
const SMS_PROFILE_ID = "sms";

const INBOX_PAGE_LIMIT = 500;
const GROUP_PAGE_LIMIT = 200;

const VALID_THREAD_KINDS = new Set(["dm", "group_chat", "match_alert"]);

/* ─────────────────────────────────────────────────────────
   Phase 1: subscribe to DM threads via collectionGroup("inbox").
   Path-prefix filter keeps us to this user's threads only.
   ───────────────────────────────────────────────────────── */
function useDmThreads(uid) {
  const [rows, setRows] = useState([]);
  // 2026-05-08: bot unread is accumulated from the SAME snapshot pass
  // (the bot's threads live in the same `inbox/` subcollection — they
  // were just hard-skipped from the DM rows). Surfaced separately so
  // the LEX AI Lex Bot tab can render its own pill without a second
  // collectionGroup subscription.
  const [botUnread, setBotUnread] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!uid) {
      setRows([]);
      setBotUnread(0);
      setReady(true);
      return undefined;
    }
    setReady(false);
    const cgPrefix = `userConversations/${uid}/`;
    const qRef = query(collectionGroup(db, "inbox"), qLimit(INBOX_PAGE_LIMIT));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const out = [];
        let bot = 0;
        snap.forEach((d) => {
          const path = String(d?.ref?.path || "");
          if (!path.startsWith(cgPrefix)) return;
          const segs = path.split("/");
          if (
            segs.length !== 6 ||
            segs[0] !== "userConversations" ||
            segs[2] !== "profiles" ||
            segs[4] !== "inbox"
          ) {
            return;
          }
          const data = d.data() || {};
          const peerUid = String(data?.peerUid || "");
          if (peerUid === LEX_BOT_PEER_UID) {
            // Accumulate bot unread separately, then return — bot
            // threads must NOT show up in DM rows (they have their
            // own LEX AI tab).
            bot += Math.max(0, Number(data?.unreadCount || 0));
            return;
          }
          const myProfileId = segs[3];
          // 2026-05-19: drop SMS threads — they belong to the Phone
          // tab, not the Inbox DMs. See SMS_PROFILE_ID note above.
          if (myProfileId === SMS_PROFILE_ID) {
            return;
          }
          const cid = segs[5];
          const lastAtMs =
            Number(data?.lastAtMs) ||
            (data?.lastAt && typeof data.lastAt.toMillis === "function"
              ? data.lastAt.toMillis()
              : 0) ||
            Number(data?.updatedAtMs) ||
            0;
          out.push({
            id: `${myProfileId}::${cid}`,
            kind: "dm",
            cid,
            myProfileId,
            peerUid,
            peerProfileId: String(data?.peerProfileId || ""),
            peerName: String(data?.peerNameCache || data?.peerName || ""),
            peerAvatar: String(data?.peerAvatarCache || ""),
            peerKind: data?.peerKind || null,
            isGroup: !!data?.isGroup,
            threadKind: data?.threadKind || null,
            unreadCount: Math.max(0, Number(data?.unreadCount || 0)),
            lastText: String(data?.lastText || ""),
            lastFromViewer:
              typeof data?.lastFromViewer === "boolean"
                ? data.lastFromViewer
                : data?.lastSenderUid
                ? data.lastSenderUid === uid
                : null,
            lastAtMs,
            scopeAppId: String(data?.scopeAppId || data?.appId || ""),
            scopeOwnerUid: String(data?.scopeOwnerUid || data?.ownerUid || ""),
            templateId: String(data?.templateId || ""),
            archived: !!data?.archived,
            lexThreadKind: VALID_THREAD_KINDS.has(data?.lexThreadKind)
              ? data.lexThreadKind
              : null,
          });
        });
        setRows(out.filter((r) => !r.archived));
        setBotUnread(bot);
        setReady(true);
      },
      (err) => {
        console.warn("[unified-unread] DM subscribe failed:", err?.message || err);
        setRows([]);
        setBotUnread(0);
        setReady(true);
      }
    );
    return () => {
      try { unsub?.(); } catch {}
    };
  }, [uid]);

  return { rows, botUnread, ready };
}

/* ─────────────────────────────────────────────────────────
   Phase 2: subscribe to per-user group state under
   userConversations/{uid}/groups. This holds unreadCount and
   read-anchor timestamps but NOT the appId / lastText / appName —
   those live in the global appGroups/{gid} doc, fetched in phase 3.
   ───────────────────────────────────────────────────────── */
function useUserGroupStates(uid) {
  const [byGid, setByGid] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!uid) {
      setByGid({});
      setReady(true);
      return undefined;
    }
    setReady(false);
    const qRef = query(
      collection(db, "userConversations", uid, "groups"),
      qLimit(GROUP_PAGE_LIMIT)
    );
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const out = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          out[d.id] = {
            gid: d.id,
            unreadCount: Math.max(0, Number(data?.unreadCount || 0)),
            clearedAtMs: Number(data?.clearedAtMs || 0),
            lastReadAtMs: Number(data?.lastReadAtMs || 0),
            archived: !!data?.archived,
          };
        });
        setByGid(out);
        setReady(true);
      },
      (err) => {
        console.warn("[unified-unread] groups subscribe failed:", err?.message || err);
        setByGid({});
        setReady(true);
      }
    );
    return () => {
      try { unsub?.(); } catch {}
    };
  }, [uid]);

  return { byGid, ready };
}

/* ─────────────────────────────────────────────────────────
   Phase 3: for each group the user is in, subscribe to the global
   appGroups/{gid} doc to get appId / appName / lastText / lastAtMs.
   We snapshot one listener per group; on a typical user this is
   ≤ 20 listeners, well within Firestore's per-tab budget.
   ───────────────────────────────────────────────────────── */
function useGroupDocs(gids) {
  const [byGid, setByGid] = useState({});
  // Use a STABLE key so we don't re-subscribe on every render.
  const stableKey = useMemo(
    () => Array.from(new Set(gids)).sort().join("|"),
    [gids]
  );

  useEffect(() => {
    if (!stableKey) {
      setByGid({});
      return undefined;
    }
    const ids = stableKey.split("|").filter(Boolean);
    const unsubs = ids.map((gid) => {
      const ref = doc(db, "appGroups", gid);
      return onSnapshot(
        ref,
        (snap) => {
          if (!snap.exists()) {
            setByGid((prev) => {
              if (!prev[gid]) return prev;
              const { [gid]: _omit, ...rest } = prev;
              return rest;
            });
            return;
          }
          const data = snap.data() || {};
          setByGid((prev) => ({
            ...prev,
            [gid]: {
              gid,
              appId: String(data?.appId || ""),
              ownerUid: String(data?.ownerUid || ""),
              appName: String(data?.appName || "Group Messages"),
              appLogoUrl: String(data?.appLogoUrl || ""),
              lastText: String(data?.lastText || ""),
              lastAtMs:
                Number(data?.lastAtMs) ||
                (data?.lastAt && typeof data.lastAt.toMillis === "function"
                  ? data.lastAt.toMillis()
                  : 0),
              memberCount: Number(data?.memberCount || 0),
            },
          }));
        },
        () => {}
      );
    });
    return () => {
      unsubs.forEach((u) => {
        try { u(); } catch {}
      });
    };
  }, [stableKey]);

  return byGid;
}

/* ─────────────────────────────────────────────────────────
   Public hook: useUnifiedUnread(uid)

   Returns:
     {
       ready: boolean,
       threads: [
         {
           id, kind ∈ "dm" | "group", scopeAppId, scopeOwnerUid,
           peerName, peerAvatar, unreadCount, lastText, lastAtMs,
           ...kind-specific fields
         },
         ...
       ],
       byApp: { [appId]: { dm: number, group: number, total: number } },
       total: { dm: number, group: number, total: number }
     }
   ───────────────────────────────────────────────────────── */
export function useUnifiedUnread(uid) {
  const { rows: dmRows, botUnread, ready: dmReady } = useDmThreads(uid);
  const { byGid: groupStates, ready: groupReady } = useUserGroupStates(uid);
  const groupGids = useMemo(() => Object.keys(groupStates), [groupStates]);
  const groupDocs = useGroupDocs(groupGids);

  // Build group thread rows.
  //
  // 2026-05-09: the primary source of truth for `unreadCount` is now
  // the server-side groupChatFanOut Cloud Function
  // (functions/groupChatFanOut.js), which writes the real per-user
  // count on every group message via FieldValue.increment(1). So the
  // `stored` value below is normally the actual number.
  //
  // We keep the `stored > 0 ? stored : (lastAtMs > anchor ? 1 : 0)`
  // fallback for two defensive reasons:
  //   1. Legacy groups whose messages predate the fan-out function:
  //      the global appGroups doc still snapshots their `lastAtMs`,
  //      so the fallback at least surfaces "something is new" as a
  //      single dot.
  //   2. The brief window between the client write
  //      (appGroups/{gid}/messages/{mid} create) and the Cloud
  //      Function commit (recipient state update). Snapshot order is
  //      not guaranteed; the fallback ensures the recipient sees a
  //      "1" within ~1 frame even if the increment write hasn't
  //      propagated yet.
  const groupThreads = useMemo(() => {
    // 2026-05-09 (semantics fix): no longer filter on archived for
    // group threads. The previous Clear-all path in lexAiInbox.js was
    // writing archived:true to per-user group state docs, which
    // matched user intent for DMs ("delete = forget this person") but
    // NOT for groups ("delete = clear old messages, keep the
    // channel"). Now Clear all writes clearedAtMs instead, and the
    // archived bit becomes a no-op for groups. Removing the filter
    // also retroactively resurrects every group state doc that was
    // buried by the broken Clear all — they reappear with empty
    // preview (canShowPreview = lastAtMs > clearedAtMs) until the
    // next message arrives. DM threads keep their archived filter in
    // useDmThreads — the semantics there is genuinely "I deleted
    // this conversation".
    return Object.values(groupStates)
      .map((state) => {
        const docInfo = groupDocs[state.gid] || {};
        const lastAtMs = Number(docInfo?.lastAtMs || 0);
        const stored = state.unreadCount;
        const anchor = Math.max(state.clearedAtMs, state.lastReadAtMs);
        const unread = stored > 0 ? stored : lastAtMs > anchor ? 1 : 0;
        const canShowPreview = lastAtMs > state.clearedAtMs;
        return {
          id: `group::${state.gid}`,
          kind: "group",
          gid: state.gid,
          // Make group threads compatible with the same shape the
          // DM rows use, so any consumer (LexAI Inbox row, AppStore
          // counter) can read them uniformly.
          cid: state.gid,
          myProfileId: "",
          peerUid: "__group__",
          peerProfileId: "",
          peerName: docInfo.appName || "Group Messages",
          peerAvatar: docInfo.appLogoUrl || "",
          peerKind: "group",
          isGroup: true,
          threadKind: "group",
          unreadCount: unread,
          lastText: canShowPreview ? docInfo.lastText || "" : "",
          lastFromViewer: null,
          lastAtMs,
          scopeAppId: docInfo.appId || "",
          scopeOwnerUid: docInfo.ownerUid || "",
          templateId: "",
          archived: false,
          lexThreadKind: "group_chat",
          memberCount: docInfo.memberCount || 0,
        };
      });
  }, [groupStates, groupDocs]);

  const threads = useMemo(() => {
    return [...dmRows, ...groupThreads].sort(
      (a, b) => b.lastAtMs - a.lastAtMs
    );
  }, [dmRows, groupThreads]);

  // Per-app aggregate { dm, group, total } so AppStore / Inbox pill
  // can pull a single number per app.
  const byApp = useMemo(() => {
    const out = Object.create(null);
    threads.forEach((t) => {
      const appId = t.scopeAppId;
      if (!appId) return;
      if (!out[appId]) out[appId] = { dm: 0, group: 0, total: 0 };
      const n = Number(t.unreadCount || 0);
      if (!n) return;
      if (t.kind === "dm") out[appId].dm += n;
      else if (t.kind === "group") out[appId].group += n;
      out[appId].total += n;
    });
    return out;
  }, [threads]);

  const total = useMemo(() => {
    let dm = 0;
    let group = 0;
    threads.forEach((t) => {
      const n = Number(t.unreadCount || 0);
      if (!n) return;
      if (t.kind === "dm") dm += n;
      else if (t.kind === "group") group += n;
    });
    // 2026-05-08: bot is reported alongside dm/group/total but does
    // NOT roll up into `total` — the LEX AI topbar's main badge is
    // about peer messages (DMs + groups), and bot has its own tab
    // pill. Consumers that want everything in one number can
    // compute `total + bot` themselves.
    const bot = Math.max(0, Number(botUnread || 0));
    return { dm, group, bot, total: dm + group };
  }, [threads, botUnread]);

  return {
    ready: dmReady && groupReady,
    threads,
    byApp,
    total,
  };
}
